import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import axiosInstance from '../../axiosInstance';
import ChampMotDePasse from '../Auth/ChampMotDePasse';
import EcranAuth from '../Auth/EcranAuth';
import { EXIGENCE, defautDuMotDePasse } from '../Auth/regles';

/**
 * Choisir un nouveau mot de passe, depuis le lien reçu par email.
 *
 * Le jeton n'est vérifié qu'au moment de l'envoi : le valider d'avance
 * obligerait à un aller-retour dont le seul effet serait d'apprendre à un
 * visiteur si un jeton deviné est bon.
 */
function NouveauMotDePasse() {
  const { uidb64, token } = useParams();
  const naviguer = useNavigate();

  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState('');
  const [perime, setPerime] = useState(false);
  const [fait, setFait] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const defaut = defautDuMotDePasse(motDePasse);
  const discordant = confirmation !== '' && motDePasse !== confirmation;
  const complet = motDePasse && !defaut && motDePasse === confirmation;

  const soumettre = async (e) => {
    e.preventDefault();
    setOccupe(true);
    setErreur('');
    try {
      await axiosInstance.post('password_reset/confirm/', {
        uidb64, token, password: motDePasse,
      });
      setFait(true);
      setTimeout(() => naviguer('/login'), 2500);
    } catch (error) {
      const donnees = error.response?.data;
      if (error.response?.status === 400 && donnees?.code === 'jeton_invalide') {
        setPerime(true);
      } else {
        setErreur(donnees?.password?.[0] || donnees?.detail
          || "Le changement n'a pas abouti. Réessayez.");
      }
    } finally {
      setOccupe(false);
    }
  };

  if (perime) {
    return (
      <EcranAuth titre="Ce lien n'est plus valable">
        <Stack spacing={3}>
          <Alert severity="warning">
            Un lien de réinitialisation expire après 24 heures et ne sert
            qu'une fois. Celui-ci a déjà servi, ou il est trop ancien.
          </Alert>
          <Button component={Link} to="/mot-de-passe-oublie"
                  variant="contained">
            Demander un nouveau lien
          </Button>
        </Stack>
      </EcranAuth>
    );
  }

  if (fait) {
    return (
      <EcranAuth titre="C'est fait">
        <Stack spacing={3}>
          <Alert severity="success">
            Votre mot de passe est changé. Vous allez être conduit vers la
            page de connexion.
          </Alert>
          <Button component={Link} to="/login" variant="outlined">
            Se connecter maintenant
          </Button>
        </Stack>
      </EcranAuth>
    );
  }

  return (
    <EcranAuth titre="Nouveau mot de passe"
               sous_titre="Choisissez-en un que vous retiendrez">
      <Box component="form" onSubmit={soumettre} noValidate>
        {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

        <Stack spacing={2.5}>
          <ChampMotDePasse
            label="Nouveau mot de passe"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
            fullWidth
            error={Boolean(defaut)}
            helperText={defaut || EXIGENCE}
          />

          <ChampMotDePasse
            label="Confirmer le mot de passe"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            error={discordant}
            helperText={discordant ? 'Les deux saisies diffèrent.' : ' '}
          />

          <Button type="submit" variant="contained" size="large"
                  disabled={occupe || !complet}>
            {occupe ? 'Enregistrement…' : 'Enregistrer'}
          </Button>

          <Typography variant="body2" color="text.secondary"
                      sx={{ textAlign: 'center' }}>
            <Box component={Link} to="/login" sx={{ color: 'inherit' }}>
              Retour à la connexion
            </Box>
          </Typography>
        </Stack>
      </Box>
    </EcranAuth>
  );
}

export default NouveauMotDePasse;
